import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ModelProvider } from './context/ModelContext';
import { AICoachProvider } from './context/AICoachContext';
import { ReportWriterProvider } from './context/ReportWriterContext'; // Import the provider

// Ant Design
import { ConfigProvider, App as AntApp } from 'antd';
import antdTheme from './config/antdTheme';

// Auth Components
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
// import Register from './components/auth/Register';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Layout Components
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import SkipNav from './components/layout/SkipNav';

// Common Components
import OnboardingTour from './components/common/OnboardingTour';

// Page Components
import AICoach from './components/ai-coach/AICoach';
// AI Coach V2 retired from the nav/routes per REQ-001 (D1); kept dormant + reversible.
// import AICoachV2 from './components/ai-coach/AICoachV2'; // POC with Ant Design X
import CoreHypothesis from './components/core-hypothesis/CoreHypothesis';
import QuestionEvaluator from './components/question-evaluator/QuestionEvaluator';
import ReportWriter from './components/report-writer/ReportWriter';
import ReportWriterV2 from './components/report-writer/ReportWriterV2';
import Archive from './components/archive/Archive';
import ProjectDetailPage from './components/archive/ProjectDetailPage';
import UserManagement from './components/admin/UserManagement';
import Feedback from './components/admin/Feedback';
import TenantManagementPage from './components/admin/TenantManagementPage';
import TenantDetailsPage from './components/admin/TenantDetailsPage';
import ContentManagement from './components/admin/ContentManagement';
import HelpPage from './pages/HelpPage';

// Styles
import './styles/auth.css';
import './styles/ai-coach.css';
import './styles/core-hypothesis.css';
import './styles/question-evaluator.css';
import './styles/archive.css';
import './styles/accessibility.css';

import './components/common/FeedbackButtons.css';
import './components/common/FeedbackModal.css';
import './components/common/MarkdownRenderer.css';

// Layout wrapper for protected routes with header and sidebar
const ProtectedLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleSidebarToggle = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  return (
    <>
      <SkipNav />
      <div className="app-layout">
        <Sidebar onToggle={handleSidebarToggle} />
        <div className={`main-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Header />
          <main
            id="main-content"
            role="main"
            aria-label="Main content"
            className="main-content"
          >
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
};

// CLEAR CONVERSATION MEMORY
function App() {
  return (
    <ConfigProvider theme={antdTheme}>
      <AntApp>
        <AuthProvider>
          <ModelProvider>
            <AICoachProvider>
              <ReportWriterProvider>
                <Router>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    {/* <Route path="/register" element={<Register />} /> */}

                    {/* Help Center - standalone page in new window */}
                    <Route path="/help" element={<HelpPage />} />

                    {/* Protected routes with layout */}
                    <Route element={<ProtectedRoute />}>
                      <Route element={<ProtectedLayout />}>
                        {/* Productivity Tools (REQ-001) — replace AI Coach in the nav */}
                        <Route path="/core-hypothesis" element={<CoreHypothesis />} />
                        <Route path="/question-evaluator" element={<QuestionEvaluator />} />
                        {/* AI Coach retired from nav/routes per REQ-001 (D1); dormant + reversible. */}
                        {/* <Route path="/ai-coach" element={<AICoachV2 />} /> */}
                        <Route path="/ai-coach-v1" element={<AICoach />} />
                        <Route path="/report-writer" element={<ReportWriterV2 />} />
                        <Route path="/report-writer-v1" element={<ReportWriter />} />
                        <Route path="/archive" element={<Archive />} />
                        <Route path="/archive/:projectId" element={<ProjectDetailPage />} />

                        {/* Legacy route - redirect to new route */}
                        <Route path="/user-admin" element={<Navigate to="/user-management" replace />} />

                        {/* New admin routes */}
                        <Route path="/user-management" element={<UserManagement />} />
                        <Route path="/feedback" element={<Feedback />} />
                        <Route path="/tenant-management" element={<TenantManagementPage />} />
                        <Route path="/tenant-management/:tenantId" element={<TenantDetailsPage />} />
                        <Route path="/content-management" element={<ContentManagement />} />
                      </Route>
                    </Route>

                    {/* Default landing for authenticated users (REQ-001: Core Hypothesis) */}
                    <Route path="*" element={<Navigate to="/core-hypothesis" replace />} />
                  </Routes>

                  {/* Onboarding Tour for first-time users */}
                  <OnboardingTour />
                </Router>
              </ReportWriterProvider>
            </AICoachProvider>
          </ModelProvider>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
}


export default App;